/**
 * 页面访问权限配置
 * 定义权限项和路由的映射关系
 * 基于侧边栏菜单结构进行树状配置
 */

/**
 * 权限项配置接口（支持树状结构）
 */
export interface PermissionItem {
  /** 权限ID，用于API保存和查询 */
  id: string
  /** 权限显示名称 */
  label: string
  /** 权限描述 */
  description?: string
  /** 该权限控制的路由路径列表，支持通配符 */
  routes: string[]
  /** 图标（可选） */
  icon?: string
  /** 子权限项（树状结构） */
  children?: PermissionItem[]
}

/**
 * 页面访问权限配置列表
 * 基于侧边栏菜单结构，保持树状层级
 */
export const PAGE_ACCESS_PERMISSIONS: PermissionItem[] = [
  // ===== 侧边栏主菜单项 =====
  {
    id: 'home',
    label: 'Home',
    description: 'Main dashboard and overview',
    icon: 'lucide:home',
    routes: [
      '/home',
      '/home/*',
      '/dashboard',
      '/dashboard/*',
    ],
  },
  {
    id: 'ai-agent',
    label: 'Ally AI Agent',
    description: 'AI-powered analytics assistant',
    icon: 'lucide:bot',
    routes: [
      '/ai-agent',
      '/ai-agent/*',
    ],
    children: [
      {
        id: 'ai-agent-chat',
        label: 'Chat',
        description: 'AI chat interface',
        routes: ['/ai-agent/chat', '/ai-agent/chat/*'],
      },
    ],
  },
  {
    id: 'ally-claw',
    label: 'Ally',
    description: 'Ally — AI marketing analyst entry, dashboard, and onboarding',
    icon: 'lucide:bot',
    routes: [
      '/ally-claw',
      '/ally-claw/*',
    ],
  },
  {
    id: 'connections',
    label: 'Connections',
    description: 'Manage data sources and destinations',
    icon: 'lucide:plug',
    routes: [
      '/integrations',
      '/integrations/*',
      '/conversion-feed',
      '/conversion-feed/*',
    ],
    children: [
      {
        id: 'connections-sources',
        label: 'Sources',
        description: 'Data source integrations',
        routes: ['/integrations', '/integrations/*'],
      },
      {
        id: 'connections-destinations',
        label: 'Destinations',
        description: 'Conversion feed destinations',
        routes: ['/conversion-feed', '/conversion-feed/*'],
      },
    ],
  },
  {
    id: 'live-events',
    label: 'Live Events',
    description: 'Real-time customer event tracking',
    icon: 'lucide:activity',
    routes: [
      '/live-events',
      '/live-events/*',
    ],
  },
  {
    id: 'benchmarks',
    label: 'Benchmarks',
    description: 'Anonymous cross-store performance benchmarks',
    icon: 'lucide:bar-chart-3',
    routes: [
      '/benchmarks',
      '/benchmarks/*',
    ],
  },
  {
    id: 'attribution',
    label: 'Attribution',
    description: 'Attribution reports and analytics',
    icon: 'lucide:bar-chart-3',
    routes: [
      '/attribution',
      '/attribution/*',
    ],
    children: [
      {
        id: 'attribution-all',
        label: 'All',
        description: 'All attribution data',
        routes: ['/attribution/all', '/attribution/all/*'],
      },
      {
        id: 'attribution-ads',
        label: 'Ads',
        description: 'Ad attribution analytics',
        routes: ['/attribution/ads', '/attribution/ads/*'],
      },
      {
        id: 'attribution-conversion-path',
        label: 'Conversion Paths',
        description: 'Customer conversion journey',
        routes: ['/attribution/conversion-path', '/attribution/conversion-path/*'],
      },
      {
        id: 'attribution-product-analytics',
        label: 'Product Analytics',
        description: 'Product performance analysis',
        routes: ['/attribution/product-analytics', '/attribution/product-analytics/*'],
      },
      {
        id: 'attribution-web-analysis',
        label: 'Web Analytics',
        description: 'Web traffic and session analytics',
        routes: [
          '/attribution/web-analysis',
          '/attribution/web-analysis/*',
          '/attribution/web-analysis-v1',
          '/attribution/web-analysis-v1/*',
          '/attribution/explorer-web-analysis',
          '/attribution/explorer-web-analysis/*',
        ],
      },
    ],
  },
  {
    id: 'tracking-links',
    label: 'Tracking Links',
    description: 'Manage tracking links and influencers',
    icon: 'lucide:link',
    routes: [
      '/influencer',
      '/influencer/*',
      '/link-builder',
      '/link-builder/*',
    ],
    children: [
      {
        id: 'tracking-influencers',
        label: 'Influencers',
        description: 'Influencer marketing management',
        routes: ['/influencer', '/influencer/*'],
      },
      {
        id: 'tracking-link-builder',
        label: 'Link Builder',
        description: 'Create tracking links',
        routes: ['/link-builder', '/link-builder/*'],
      },
    ],
  },
  {
    id: 'store',
    label: 'Store',
    description: 'Store data and customer analytics',
    icon: 'lucide:store',
    routes: [
      '/store',
      '/store/*',
    ],
    children: [
      {
        id: 'store-segments',
        label: 'Segments',
        description: 'Customer segmentation',
        routes: ['/store/segments', '/store/segments/*', '/store/segments-v1', '/store/segments-v1/*'],
      },
      {
        id: 'store-orders',
        label: 'Orders',
        description: 'Order management',
        routes: ['/store/orders', '/store/orders/*'],
      },
      {
        id: 'store-customers',
        label: 'Customers',
        description: 'Customer data',
        routes: ['/store/customers', '/store/customers/*'],
      },
    ],
  },
  // ===== Settings 基于 SettingsNavigation 配置 =====
  {
    id: 'settings',
    label: 'Settings',
    description: 'Account and organization settings',
    icon: 'lucide:settings',
    routes: [
      '/settings',
      '/settings/*',
    ],
    children: [
      {
        id: 'settings-general',
        label: 'General',
        description: 'Global account preferences',
        icon: 'lucide:sliders',
        routes: ['/settings/general', '/settings/general/*'],
      },
      {
        id: 'settings-attribution-model',
        label: 'Attribution Model',
        description: 'Set default attribution model and window',
        icon: 'lucide:target',
        routes: ['/settings/attribution-model', '/settings/attribution-model/*'],
      },
      {
        id: 'settings-conversion-goals',
        label: 'Conversion Goals',
        description: 'Custom conversion goal settings',
        icon: 'lucide:goal',
        routes: ['/settings/conversion-goals', '/settings/conversion-goals/*'],
      },
      {
        id: 'settings-api-keys',
        label: 'API Keys',
        description: 'API key management',
        icon: 'lucide:key',
        routes: ['/settings/api-keys', '/settings/api-keys/*'],
      },
      {
        id: 'settings-exclusion-list',
        label: 'Exclusion List',
        description: 'Ignore events from attribution',
        icon: 'lucide:list-x',
        routes: ['/settings/exclusion-list', '/settings/exclusion-list/*'],
      },
      {
        id: 'settings-pixel-setting',
        label: 'Pixel Settings',
        description: 'Pixel traffic exclusion rules',
        icon: 'lucide:settings-2',
        routes: ['/settings/pixel-setting', '/settings/pixel-setting/*'],
      },
      {
        id: 'settings-custom-domain',
        label: 'Custom Domain',
        description: 'Brand domain for tracking links',
        icon: 'lucide:globe',
        routes: ['/settings/custom-domain', '/settings/custom-domain/*'],
      },
      {
        id: 'settings-labs',
        label: 'Labs',
        description: 'Experimental developer tools',
        icon: 'lucide:flask-conical',
        routes: ['/settings/labs', '/settings/labs/*'],
      },
      {
        id: 'settings-channel-group',
        label: 'Channel Group',
        description: 'Manage channel groupings',
        icon: 'lucide:layers',
        routes: ['/settings/channel-group', '/settings/channel-group/*'],
      },
      {
        id: 'settings-cost-settings',
        label: 'Cost Settings',
        description: 'Profit calculation settings',
        icon: 'lucide:dollar-sign',
        routes: ['/settings/cost-settings', '/settings/cost-settings/*'],
      },
      {
        id: 'settings-aan',
        label: 'Capture Anonymous Visitors',
        description: 'Audience network settings',
        icon: 'lucide:users',
        routes: ['/settings/aan', '/settings/aan/*'],
      },
      {
        id: 'settings-account',
        label: 'Account',
        description: 'Team members and permissions',
        icon: 'lucide:user-cog',
        routes: ['/settings/account', '/settings/account/*'],
      },
    ],
  },
]

/**
 * 扁平化权限列表（包含所有子权限）
 * 用于UI展示等
 */
function flattenPermissions(items: PermissionItem[]): PermissionItem[] {
  const result: PermissionItem[] = []
  for (const item of items) {
    result.push(item)
    if (item.children) {
      result.push(...flattenPermissions(item.children))
    }
  }
  return result
}

/**
 * 获取叶子节点权限列表（没有子权限的权限项）
 * 只有叶子节点用于实际的路由匹配
 */
function getLeafPermissions(items: PermissionItem[]): PermissionItem[] {
  const result: PermissionItem[] = []
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      // 有子项，递归处理子项
      result.push(...getLeafPermissions(item.children))
    }
    else {
      // 叶子节点
      result.push(item)
    }
  }
  return result
}

/** 扁平化的权限列表缓存（包含所有节点） */
const FLAT_PERMISSIONS = flattenPermissions(PAGE_ACCESS_PERMISSIONS)

/** 叶子节点权限列表缓存（仅用于路由匹配） */
const LEAF_PERMISSIONS = getLeafPermissions(PAGE_ACCESS_PERMISSIONS)

/**
 * 通配符路径匹配工具
 * @param pattern 路由模式，支持 * 通配符
 * @param path 实际路径
 * @returns 匹配结果：0=不匹配，1=通配符匹配，2=精确匹配
 */
export function matchRoute(pattern: string, path: string): number {
  // 精确匹配
  if (pattern === path)
    return 2

  // 通配符匹配
  if (pattern.endsWith('/*')) {
    const basePattern = pattern.slice(0, -2) // 去掉 /*
    if (path === basePattern || path.startsWith(`${basePattern}/`))
      return 1
  }

  return 0
}

/**
 * 根据路由路径查找对应的权限项
 * 只使用叶子节点进行匹配，父级权限仅用于UI展示和批量选择
 * @param path 路由路径
 * @returns 该路由需要的权限项ID列表
 */
export function getRequiredPermissions(path: string): string[] {
  const exactMatchPermissions: string[] = []
  const wildcardMatchPermissions: string[] = []

  // 只使用叶子节点进行匹配
  for (const permission of LEAF_PERMISSIONS) {
    let hasExactMatch = false
    let hasWildcardMatch = false

    for (const route of permission.routes) {
      const matchResult = matchRoute(route, path)
      if (matchResult === 2) {
        hasExactMatch = true
        break // 精确匹配优先级最高
      }
      else if (matchResult === 1) {
        hasWildcardMatch = true
      }
    }

    if (hasExactMatch) {
      exactMatchPermissions.push(permission.id)
    }
    else if (hasWildcardMatch) {
      wildcardMatchPermissions.push(permission.id)
    }
  }

  // 如果有精确匹配的权限，只返回精确匹配的权限
  if (exactMatchPermissions.length > 0) {
    return exactMatchPermissions
  }

  // 否则返回通配符匹配的权限
  return wildcardMatchPermissions
}

/**
 * 检查用户是否有访问指定路由的权限
 * @param path 路由路径
 * @param userPermissions 用户拥有的权限ID列表（如果为空或undefined，表示拥有全部权限）
 * @returns 是否有权限访问
 */
export function checkRouteAccess(path: string, userPermissions?: string[]): boolean {
  // 如果用户权限为空或undefined，表示拥有全部权限
  if (!userPermissions || userPermissions.length === 0) {
    return true
  }

  // 获取该路由需要的权限
  const requiredPermissions = getRequiredPermissions(path)

  // 如果路由不需要任何权限（未配置），则允许访问
  if (requiredPermissions.length === 0) {
    return true
  }

  // 检查用户是否拥有任一所需权限（只要有一个匹配即可访问）
  return requiredPermissions.some(required => userPermissions.includes(required))
}

/**
 * 获取所有可用的权限项（用于设置界面）
 * 返回树状结构的权限列表
 */
export function getAllPermissions(): PermissionItem[] {
  return PAGE_ACCESS_PERMISSIONS
}

/**
 * 获取扁平化的所有权限项
 */
export function getFlatPermissions(): PermissionItem[] {
  return FLAT_PERMISSIONS
}

/**
 * 获取叶子节点权限项（用于实际的路由匹配）
 */
export function getLeafPermissionsList(): PermissionItem[] {
  return LEAF_PERMISSIONS
}

/**
 * 根据权限ID获取权限项详情（在扁平化列表中查找）
 */
export function getPermissionById(id: string): PermissionItem | undefined {
  return FLAT_PERMISSIONS.find(p => p.id === id)
}

/**
 * 获取权限项及其所有子权限的ID列表
 */
export function getPermissionWithChildrenIds(permissionId: string): string[] {
  const ids: string[] = [permissionId]

  function collectChildIds(items: PermissionItem[]) {
    for (const item of items) {
      if (item.id === permissionId && item.children) {
        // 找到目标权限，收集其所有子权限ID
        const collectAllChildren = (children: PermissionItem[]) => {
          for (const child of children) {
            ids.push(child.id)
            if (child.children) {
              collectAllChildren(child.children)
            }
          }
        }
        collectAllChildren(item.children)
        return
      }
      if (item.children) {
        collectChildIds(item.children)
      }
    }
  }

  collectChildIds(PAGE_ACCESS_PERMISSIONS)
  return ids
}
